import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import OfflineSidebar from './OfflineSidebar';
import OfflineInteractiveMap from './OfflineInteractiveMap';
import SaveLayerModal from '../components/SaveLayerModal';
import logo from '../assets/logo128x128.png';
import 'leaflet/dist/leaflet.css';
import '../components/leaflet-geoman.css';
import './MapView.css';
import { getOfflineMap, saveOfflineMap as _saveOfflineMap } from '../utils/offlineDb';
import Styler from '../components/styles/Styler';

function saveOfflineMap(id, obj, onSaved) {
  if (obj && typeof obj === "object" && 'name' in obj && !('poi_name' in obj)) {
    console.error('saveOfflineMap called with object containing "name" field instead of "poi_name":', { id, obj });
  }
  return _saveOfflineMap(id, obj).then((res) => {
    if (onSaved) {
      onSaved();
    }
    return res;
  }).catch(err => {
    throw err;
  });
}

function useMobile(threshold = 800) {
  const [mobile, setMobile] = useState(window.innerWidth <= threshold);
  useEffect(() => {
    const handleResize = () => {
      const isMobileNow = window.innerWidth <= threshold;
      setMobile(isMobileNow);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [threshold]);
  return mobile;
}

function promptUserToMoveTypesAndPois(oldCatName, newCatName) {
  return new Promise(resolve => {
    const confirmed = window.confirm(
      `You created new category "${newCatName}".\n` +
      `Do you want to move all types and POIs from "${oldCatName}" to "${newCatName}"?`
    );
    resolve(confirmed);
  });
}

async function scanAndCleanIndexedDBMapData(mapId, setCategories, setTypes, setOfflinePois) {
  const bundle = await getOfflineMap(mapId);
  if (!bundle) return;

  const pois = Array.isArray(bundle.pois) ? bundle.pois : [];
  const types = Array.isArray(bundle.poi_types) ? bundle.poi_types : [];
  const categories = Array.isArray(bundle.poi_categories) ? bundle.poi_categories : [];

  const referencedTypeIds = new Set(pois.map(p => p.type_id).filter(id => id != null));
  const cleanedTypes = types.filter(t => referencedTypeIds.has(t.id));

  const referencedCategoryIds = new Set(cleanedTypes.map(t => t.category_id).filter(id => id != null));
  const cleanedCategories = categories.filter(c => referencedCategoryIds.has(c.id));

  const newBundle = {
    ...bundle,
    pois,
    poi_types: cleanedTypes,
    poi_categories: cleanedCategories,
    lastEdited: Date.now(),
  };
  await saveOfflineMap(mapId, newBundle);

  setCategories(cleanedCategories);
  setTypes(cleanedTypes);
  setOfflinePois(pois);
}

function OfflineMapView() {
  const params = useParams();
  const navigate = useNavigate();
  const offlineId = params.id;

  const [bundle, setBundle] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stylerOpen, setStylerOpen] = useState(false);
  const [stylerOfflineInfoOpen, setStylerOfflineInfoOpen] = useState(false);
  const sidebarRef = useRef(null);

  const [visibleTypes, setVisibleTypes] = useState(new Set());
  const [visibleCategories, setVisibleCategories] = useState(new Set());
  const [hoveredType, setHoveredType] = useState(null);
  const [hoveredPoiId, setHoveredPoiId] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedTypes, setExpandedTypes] = useState({});
  const [zonesExpanded, setZonesExpanded] = useState(true);
  const [visibleZones, setVisibleZones] = useState(new Set());
  const [hoveredZoneId, setHoveredZoneId] = useState(null);
  const [hideSidebarHover, setHideSidebarHover] = useState(false);
  const [extraRegionMaps] = useState([]);
  const [showAllPoiLabels, setShowAllPoiLabels] = useState(false);
  const [flashType, setFlashType] = useState(null);

  const [pendingPOI, setPendingPOI] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [description, setDescription] = useState('');
  const [poiName, setPoiName] = useState('');
  const [typeColourInput, setTypeColourInput] = useState('#ff0000');
  const [shape, setShape] = useState('circle');
  const [pendingLayer, setPendingLayer] = useState(null);
  const [showSaveLayerModal, setShowSaveLayerModal] = useState(false);
  const [mouseCoord, setMouseCoord] = useState(null);

  const [activeTool, setActiveTool] = useState(null);
  const [pencilSize, setPencilSize] = useState(4);
  const [fill, setFill] = useState('#d43a3a');
  const [opacity, setOpacity] = useState(0.7);
  const [pattern, setPattern] = useState('none');

  const isMobile = useMobile(800);
  const [mobileDrawer, setMobileDrawer] = useState(null);

  const [offlinePois, setOfflinePois] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [zones, setZones] = useState([]);

  const [offlinePoisWithNames, setOfflinePoisWithNames] = useState([]);

  const [showOfflineWarning, setShowOfflineWarning] = useState(true);

  useEffect(() => {
    const catMap = {};
    categories.forEach(c => { catMap[c.id] = c.name; });
    const typeMap = {};
    types.forEach(t => { typeMap[t.id] = t; });

    const newOfflinePoisWithNames = offlinePois.map(poi => ({
      ...poi,
      category: catMap[poi.category_id] || "",
      typeName: typeMap[poi.type_id]?.name || "",
      type: typeMap[poi.type_id]?.name || "",
      typeColour: typeMap[poi.type_id]?.color || "#ff0000",
      typeShape: typeMap[poi.type_id]?.shape || "circle",
      label_border: poi.label_border ?? typeMap[poi.type_id]?.label_border ?? 1,
      label_bg_colour: poi.label_bg_colour ?? typeMap[poi.type_id]?.label_bg_colour ?? "#000000",
    }));

    setOfflinePoisWithNames(newOfflinePoisWithNames);
  }, [offlinePois, categories, types]);

  const scanAndCleanTypesAndCategories = () => {
    const usedCategoryIds = new Set();
    offlinePois.forEach(poi => {
      if (poi.category_id != null) usedCategoryIds.add(poi.category_id);
    });
    types.forEach(type => {
      if (type.category_id != null) usedCategoryIds.add(type.category_id);
    });
    const filteredCategories = categories.filter(cat => usedCategoryIds.has(cat.id));
    const usedTypeIds = new Set();
    offlinePois.forEach(poi => {
      if (poi.type_id != null) usedTypeIds.add(poi.type_id);
    });
    const filteredTypes = types.filter(type =>
      usedTypeIds.has(type.id) && filteredCategories.some(cat => cat.id === type.category_id)
    );
    if (filteredCategories.length !== categories.length) {
      setCategories(filteredCategories);
    }
    if (filteredTypes.length !== types.length) {
      setTypes(filteredTypes);
    }
  };

  useEffect(() => {
    if (offlinePois.length) {
      scanAndCleanTypesAndCategories();
    }
  }, [offlinePois]);

  useEffect(() => {
    let currentUrl = null;
    getOfflineMap(offlineId).then(async b => {
      if (!b) {
        alert('Map not found or deleted!');
        navigate('/offline');
        return;
      }
      setBundle(b);
      if (b.image) {
        if (currentUrl) URL.revokeObjectURL(currentUrl);
        currentUrl = URL.createObjectURL(b.image);
        setImageUrl(currentUrl);
      } else {
        setImageUrl(null);
      }
      if (b.poi_types) setVisibleTypes(new Set(b.poi_types.map(t => t.id)));
      if (b.poi_categories) {
        setVisibleCategories(oldSet => {
          const next = new Set(oldSet);
          b.poi_categories.forEach(cat => {
            if (!next.has(cat.id)) {
              next.add(cat.id);
            }
          });
          return next;
        });
      }
      if (b.zones) setZones(b.zones);
      if (Array.isArray(b.pois)) {
        setOfflinePois(b.pois);
      } else setOfflinePois([]);
      setCategories(Array.isArray(b.poi_categories) ? b.poi_categories : []);
      setTypes(Array.isArray(b.poi_types) ? b.poi_types : []);
    });
    return () => { if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [offlineId, navigate]);

  useEffect(() => {
    if (!bundle) return;
    const updated = { ...bundle, pois: offlinePois, lastEdited: Date.now() };
    setBundle(updated);
  }, [offlinePois]);

  useEffect(() => {
    if (!bundle) return;
    const updated = { ...bundle, poi_categories: categories, lastEdited: Date.now() };
    setBundle(updated);
  }, [categories]);

  useEffect(() => {
    if (!bundle) return;
    const updated = { ...bundle, poi_types: types, lastEdited: Date.now() };
    setBundle(updated);
  }, [types]);

  useEffect(() => {
    if (!bundle) return;
    const updated = { ...bundle, zones: zones, lastEdited: Date.now() };
    setBundle(updated);
  }, [zones]);

  useEffect(() => {
    if (!Array.isArray(zones)) return;
    setVisibleZones(new Set(zones.map(z => z.id)));
  }, [zones]);

  const handleUpdateCategories = (newCategories) => {
    setCategories(newCategories);
    setVisibleCategories(oldVisible => {
      const nextVisible = new Set(oldVisible);
      newCategories.forEach(cat => {
        if (!nextVisible.has(cat.id)) {
          nextVisible.add(cat.id);
        }
      });
      return nextVisible;
    });
  };

  const handleUpdateTypes = (newTypes) => {
    setTypes(newTypes);
    setVisibleTypes(oldVisible => {
      const nextVisible = new Set(oldVisible);
      newTypes.forEach(type => {
        if (!nextVisible.has(type.id)) {
          nextVisible.add(type.id);
        }
      });
      return nextVisible;
    });
  };

  const saveCategory = (catObj) => {
    if (!bundle) return;
    setCategories(prev => {
      let newCats = Array.isArray(prev) ? [...prev] : [];
      let newCat = { ...catObj };
      if (newCat.id) {
        const idx = newCats.findIndex(c => c.id === newCat.id);
        if (idx !== -1) {
          newCats[idx] = newCat;
        } else {
          newCats.push(newCat);
        }
      } else {
        newCat.id = Date.now();
        newCats.push(newCat);
      }
      setVisibleCategories(old => {
        const next = new Set(old);
        next.add(newCat.id);
        return next;
      });
      return newCats;
    });
    return catObj;
  };

  const saveType = (typeObj) => {
    if (!bundle) return;
    setTypes(prev => {
      if (!Array.isArray(prev)) return [typeObj];
      const idx = prev.findIndex(t => t.id === typeObj.id);
      if (idx === -1) {
        return [...prev, typeObj];
      } else {
        const updatedTypes = [...prev];
        updatedTypes[idx] = { ...updatedTypes[idx], name: typeObj.name };
        return updatedTypes;
      }
    });

    setVisibleTypes(oldSet => {
      const next = new Set(oldSet);
      next.add(typeObj.id);
      return next;
    });

    return typeObj;
  };

  const handlePOIUpdate = async (poiObj, deletedPoiId, options) => {
    if (deletedPoiId) {
      if (!bundle) return;
      const poiToDelete = offlinePois.find(p => p.id === deletedPoiId);
      if (!poiToDelete) return;
      const newPois = offlinePois.filter(p => p.id !== deletedPoiId);
      setOfflinePois(newPois);

      const updatedBundle = { ...bundle, pois: newPois, lastEdited: Date.now() };
      setBundle(updatedBundle);
      const bundleToSave = { ...updatedBundle };
      delete bundleToSave.styles;
      await saveOfflineMap(updatedBundle.id, bundleToSave);

      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('offlineMapDataChanged', { detail: { mapId: offlineId } }));
      }, 300);
      setPendingPOI(null);
      setPoiName('');
      setSelectedCategory('');
      setSelectedType('');
      setDescription('');
      setShowSaveLayerModal(false);
      return;
    }

    if (!poiObj) return;

    if (options?.confirmedDbSave) {
      return;
    }

    const newCategoryNameRaw = poiObj.category || "";
    const newCategoryName = newCategoryNameRaw.trim();

    const oldPoi = offlinePois.find(p => p.id === poiObj.id);
    const oldCategoryId = oldPoi?.category_id || null;

    const existingCategory = categories.find(c => c.name.toLowerCase() === newCategoryName.toLowerCase());

    let categoryIdToUse = null;
    if (existingCategory) {
      categoryIdToUse = existingCategory.id;
    } else if (newCategoryName !== "") {
      categoryIdToUse = Date.now();
      const newCategory = { id: categoryIdToUse, name: newCategoryName };
      setCategories(prev => [...prev, newCategory]);

      const oldCategoryName = categories.find(c => c.id === oldCategoryId)?.name || "Unknown";
      const moveConfirmed = await promptUserToMoveTypesAndPois(oldCategoryName, newCategoryName);

      if (moveConfirmed && oldCategoryId !== null) {
        const updatedTypes = types.map(type =>
          type.category_id === oldCategoryId ? { ...type, category_id: categoryIdToUse } : type
        );
        setTypes(updatedTypes);

        const updatedPois = offlinePois.map(poi =>
          poi.category_id === oldCategoryId ? { ...poi, category_id: categoryIdToUse } : poi
        );
        setOfflinePois(updatedPois);
      }
    } else {
      categoryIdToUse = oldCategoryId;
    }

    const updatedPoi = {
      ...poiObj,
      category_id: categoryIdToUse,
    };

    let newList = Array.isArray(offlinePois) ? [...offlinePois] : [];
    const idx = newList.findIndex(p => p.id === updatedPoi.id);
    if (idx !== -1) {
      newList[idx] = updatedPoi;
    } else {
      newList.push(updatedPoi);
    }

    setOfflinePois(newList);

    const updatedBundle = { ...bundle, pois: newList, lastEdited: Date.now() };
    setBundle(updatedBundle);
    const bundleToSave = { ...updatedBundle };
    delete bundleToSave.styles;
    await saveOfflineMap(updatedBundle.id, bundleToSave);

    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('offlineMapDataChanged', { detail: { mapId: offlineId } }));
    }, 300);

    setPendingPOI(null);
    setPoiName('');
    setSelectedCategory('');
    setSelectedType('');
    setDescription('');
  };

  const saveZone = (zoneObj) => {
    if (!bundle) return;
    const newZones = [...zones];
    if (zoneObj.id) {
      const idx = newZones.findIndex(z => z.id === zoneObj.id);
      if (idx !== -1) {
        newZones[idx] = zoneObj;
      }
      else {
        newZones.push(zoneObj);
      }
    } else {
      zoneObj.id = Date.now();
      newZones.push(zoneObj);
    }
    setZones(newZones);

    const updatedBundle = { ...bundle, zones: newZones, lastEdited: Date.now() };
    setBundle(updatedBundle);
    const bundleToSave = { ...updatedBundle };
    delete bundleToSave.styles;
    saveOfflineMap(updatedBundle.id, bundleToSave);
  };

  const handleZonesChanged = () => {
    getOfflineMap(offlineId).then(b => {
      if (b && b.zones) setZones(b.zones);
    });
  };

  const saveStyles = (stylesObj) => {
  };

  const handleSidebarMouseDown = (e) => {
    const startX = e.clientX;
    const startWidth = sidebarRef.current.offsetWidth;
    const onMouseMove = (e) => {
      const newWidth = startWidth + (e.clientX - startX);
      if (newWidth >= 150 && newWidth <= 600) setSidebarWidth(newWidth);
    };
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const typesByCategory = {};
  types.forEach(type => {
    if (!type.category_id && !type.category) return;
    if (type.category_id !== undefined) {
      if (!typesByCategory[type.category_id]) typesByCategory[type.category_id] = [];
      typesByCategory[type.category_id].push(type);
    }
    if (type.category) {
      if (!typesByCategory[type.category]) typesByCategory[type.category] = [];
      typesByCategory[type.category].push(type);
    }
  });

  useEffect(() => {
    const handleOfflineMapDataChanged = async (event) => {
      const changedMapId = event.detail?.mapId;
      if (changedMapId === offlineId) {
        await scanAndCleanIndexedDBMapData(offlineId, setCategories, setTypes, setOfflinePois);
        const b = await getOfflineMap(offlineId);
        if (!b) return;
        setBundle(b);
        if (b.image) {
          const url = URL.createObjectURL(b.image);
          setImageUrl(url);
        } else {
          setImageUrl(null);
        }
        if (b.poi_categories) {
          setVisibleCategories(oldSet => {
            const next = new Set(oldSet);
            b.poi_categories.forEach(cat => {
              if (!next.has(cat.id)) {
                next.add(cat.id);
              }
            });
            return next;
          });
        }
        if (b.zones) {
          setZones(b.zones);
        }
      }
    };
    window.addEventListener('offlineMapDataChanged', handleOfflineMapDataChanged);
    return () => {
      window.removeEventListener('offlineMapDataChanged', handleOfflineMapDataChanged);
    };
  }, [offlineId]);

  if (!bundle || !imageUrl) {
    return <div style={{ padding: '2rem', color: 'white' }}>Map not found</div>;
  }

  return (
    <>
      {showOfflineWarning && (
        <div style={{
          position: "relative",
          background: "#e33",
          color: "#fff",
          padding: 12,
          textAlign: "center",
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: 0.3,
          borderBottom: "2px solid #c11"
        }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>
              LOCAL MODE – This map is stored only in your browser. It’s your private sandbox map.
            </div>
            <div style={{ fontSize: 14, opacity: 0.95 }}>
              No login is needed, but if you clear site data or switch browser/device, this map will be lost. For a permanent, shareable copy, upload it to a free Point Atlas account or make a backup export.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowOfflineWarning(false)}
            aria-label="Dismiss warning"
            style={{
              position: "absolute",
              right: 12,
              top: 8,
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: 20,
              fontWeight: 800,
              cursor: "pointer",
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}
      {(
        (!offlinePois || offlinePois.length === 0) ||
        (!categories || categories.length === 0) ||
        (!types || types.length === 0)
      ) && (
        <div style={{
          background: "#222",
          color: "#ffe089",
          padding: 12,
          textAlign: "center",
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: 0.2,
          borderBottom: "2px solid #886",
          borderTop: "2px solid #886",
          marginBottom: 0,
          zIndex: 3000
        }}>
          <div>No POIs, types, or categories found for this map.</div>
          <div style={{ fontSize: 14, marginTop: 4, fontWeight: 600 }}>
            This is normal for a new offline map – click on the map to add your first POI or import data to start filling it.
          </div>
        </div>
      )}
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          flexDirection: "row",
          background: bundle?.styles?.main_bg_colour || "#1a1c1e",
          overflow: "hidden",
          position: "relative"
        }}
      >
        {!isMobile && (
          <>
            <div style={{ display: 'flex', flexDirection: 'row', zIndex: 110, position: "relative" }}>
              {sidebarOpen && (
                <button
                  type="button"
                  style={{
                    position: "absolute",
                    top: 20,
                    left: 340,
                    zIndex: 2200,
                    background: "#23272b",
                    color: "#ffe089",
                    border: "1.5px solid #886",
                    borderRadius: 9,
                    fontWeight: 700,
                    fontSize: 15,
                    cursor: "pointer",
                    padding: "9px 18px",
                    boxShadow: "0 2px 10px #0006",
                  }}
                  onClick={() => { setStylerOfflineInfoOpen(true); }}
                  title="Customize Map Styles"
                >
                  ✏️
                </button>
              )}
              {sidebarOpen && (
                <OfflineSidebar
                  key={bundle?.id}
                  sidebarWidth={sidebarWidth}
                  sidebarRef={sidebarRef}
                  logo={logo}
                  mapDisplayName={bundle.meta?.name}
                  voteStatus={null}
                  handleVote={null}
                  user={null}
                  isFavourited={false}
                  favLoading={false}
                  handleFavourite={null}
                  customCategories={categories}
                  customTypes={types}
                  typeColours={{}}
                  visibleTypes={visibleTypes}
                  setVisibleTypes={setVisibleTypes}
                  visibleCategories={visibleCategories}
                  hoveredType={hoveredType}
                  setHoveredType={setHoveredType}
                  handleTypeHover={(type) => setHoveredType(type)}
                  handleTypeUnhover={() => setHoveredType(null)}
                  hoveredPoiId={hoveredPoiId}
                  setHoveredPoiId={setHoveredPoiId}
                  expandedCategories={expandedCategories}
                  setExpandedCategories={setExpandedCategories}
                  expandedTypes={expandedTypes}
                  setExpandedTypes={setExpandedTypes}
                  toggleCategory={(catId) => {
                    setVisibleCategories(old => {
                      const next = new Set(old);
                      if (next.has(catId)) next.delete(catId);
                      else next.add(catId);
                      return next;
                    });
                  }}
                  toggleType={(typeId) => {
                    setVisibleTypes(old => {
                      const next = new Set(old);
                      if (next.has(typeId)) next.delete(typeId);
                      else next.add(typeId);
                      return next;
                    });
                  }}
                  handleMouseDown={handleSidebarMouseDown}
                  pois={offlinePoisWithNames}
                  tipUrl={bundle.meta?.tip_url}
                  poiLabelToggleBtn={
                    <button
                      style={{
                        margin: '10px 0 10px 0',
                        background: showAllPoiLabels ? '#ffdc5e' : '#232323',
                        color: showAllPoiLabels ? '#232' : '#fff',
                        border: 'none',
                        borderRadius: 8,
                        padding: '7px 16px',
                        fontWeight: 700,
                        fontSize: 16,
                        cursor: 'pointer',
                        boxShadow: showAllPoiLabels ? '0 2px 14px #ffd70044' : 'none',
                        transition: 'background 0.16s, color 0.16s'
                      }}
                      onClick={() => { setShowAllPoiLabels(v => !v); }}
                    >
                      {showAllPoiLabels ? "Hide all POI labels" : "Show all POI labels"}
                    </button>
                  }
                  flashType={flashType}
                  setFlashType={setFlashType}
                  zones={zones}
                  visibleZones={visibleZones}
                  setVisibleZones={setVisibleZones}
                  toggleZone={() => { }}
                  toggleAllZones={() => { }}
                  zonesExpanded={zonesExpanded}
                  toggleZonesExpand={() => setZonesExpanded(v => !v)}
                  hoveredZoneId={hoveredZoneId}
                  setHoveredZoneId={setHoveredZoneId}
                  hideSidebarHover={hideSidebarHover}
                  setHideSidebarHover={setHideSidebarHover}
                  extraRegionMaps={extraRegionMaps}
                  isMapOwner={true}
                  onAddNewRegionMap={null}
                  onRegionMapClick={null}
                  parentMapId={bundle.id}
                  styles={bundle.styles || {}}
                  stylesLoading={false}
                  stylesError={null}
                />
              )}
            </div>
            <button
              onClick={() => { setSidebarOpen(v => !v); }}
              style={{
                position: 'absolute',
                left: sidebarOpen ? (sidebarWidth - -40) : 0,
                top: 100,
                zIndex: 1011,
                width: 36,
                height: 48,
                background: '#232336',
                color: '#fff',
                border: 'none',
                borderRadius: '0 14px 14px 0',
                boxShadow: '0 2px 12px #0007',
                fontWeight: 800,
                fontSize: 22,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'left 0.23s cubic-bezier(.44,1.44,.4,1.04)'
              }}
              aria-label={sidebarOpen ? "Hide Map Info" : "Show Map Info"}
            >
              <svg width="23" height="23" viewBox="0 0 23 23" style={{ display: "block" }}>
                {sidebarOpen ? (
                  <polyline points="15,6 9,11.5 15,17" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                ) : (
                  <polyline points="8,6 14,11.5 8,17" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                )}
              </svg>
            </button>
          </>
        )}

        <div
          style={{
            flex: "1 1 0%",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <OfflineInteractiveMap
            key={bundle.id}
            mapData={bundle}
            imageUrl={imageUrl}
            imageSize={bundle?.image_size ? [bundle.image_size[1], bundle.image_size[0]] : null}
            pois={offlinePoisWithNames}
            setOfflinePois={setOfflinePois}
            offlinePois={offlinePoisWithNames}
            visibleTypes={visibleTypes}
            visibleCategories={visibleCategories}
            hoveredType={hoveredType}
            hoveredPoiId={hoveredPoiId}
            setHoveredPoiId={setHoveredPoiId}
            pendingPOI={pendingPOI}
            setPendingPOI={setPendingPOI}
            isMapOwnerOrFounder={true}
            handleMapCoordClick={() => { }}
            handleClick={setPendingPOI}
            mouseCoord={mouseCoord}
            handleMouseCoord={setMouseCoord}
            customCategories={categories}
            typeColours={{}}
            customTypes={types}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            typeColourInput={typeColourInput}
            setTypeColourInput={setTypeColourInput}
            shape={shape}
            setShape={setShape}
            fill={fill}
            opacity={opacity}
            pattern={pattern}
            description={description}
            setDescription={setDescription}
            savePOI={handlePOIUpdate}
            showAllPoiLabels={showAllPoiLabels}
            setShowAllPoiLabels={setShowAllPoiLabels}
            user={null}
            mapOwnerId={null}
            poiName={poiName}
            setPoiName={setPoiName}
            flashType={flashType}
            activeTool={activeTool}
            pencilSize={pencilSize}
            pendingLayer={pendingLayer}
            visibleZoneIds={visibleZones}
            zones={zones}
            onZonesChanged={handleZonesChanged}
            hideSidebarHover={hideSidebarHover}
            setHideSidebarHover={setHideSidebarHover}
            hoveredZoneId={hoveredZoneId}
            sidebarTabOffset={sidebarOpen ? (sidebarWidth + 36) : 36}
            styles={bundle.styles || {}}
            stylesLoading={false}
            stylesError={null}
            onSaveCategory={saveCategory}
            onSaveType={saveType}
            onSavePOI={handlePOIUpdate}
          />

          <div style={{
            position: 'absolute',
            top: 2,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1200,
            background: '#222d',
            color: '#ffe670',
            fontWeight: 700,
            borderRadius: 7,
            padding: '6px 17px 6px 13px',
            fontFamily: 'monospace',
            fontSize: 15,
            pointerEvents: 'none',
            boxShadow: '0 1px 6px #0005',
            letterSpacing: 1,
            display: mouseCoord ? 'block' : 'none'
          }}>
            {mouseCoord &&
              <>X: {Math.round(mouseCoord.x)}<span style={{ margin: '0 8px' }} />Y: {Math.round(mouseCoord.y)}</>
            }
          </div>
        </div>

        {isMobile && (
          <div id="mobile-tab-bar" className="mobile-tab-bar">
            <button
              className={`mobile-tab ${mobileDrawer === "sidebar" ? "active" : ""}`}
              onClick={() => { setMobileDrawer("sidebar"); }}
            >
              <span style={{ fontSize: 26, display: "block" }}>☰</span>
            </button>
          </div>
        )}
        {isMobile && mobileDrawer && (
          <div className="mobile-drawer-overlay" onClick={() => { setMobileDrawer(null); }}>
            <div
              className="mobile-drawer"
              onClick={e => e.stopPropagation()}
              style={{ position: "relative" }}
            >
              <button
                className="sidebar-mobile-close"
                onClick={() => { setMobileDrawer(null); }}
                aria-label="Close"
                style={{
                  position: 'absolute',
                  top: 60,
                  right: 17,
                  width: 48,
                  height: 48,
                  border: 'none',
                  background: '#212326',
                  color: '#fff',
                  fontSize: '2em',
                  fontWeight: 900,
                  zIndex: 4002,
                  lineHeight: 1,
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.15s, color 0.13s',
                  boxShadow: '0 2px 14px #000b',
                }}
              >×</button>
              <OfflineSidebar
                key={bundle?.id}
                sidebarWidth={320}
                sidebarRef={sidebarRef}
                logo={logo}
                mapDisplayName={bundle.meta?.name}
                voteStatus={null}
                handleVote={null}
                user={null}
                isFavourited={false}
                favLoading={false}
                handleFavourite={null}
                customCategories={categories}
                customTypes={types}
                typeColours={{}}
                visibleTypes={visibleTypes}
                setVisibleTypes={setVisibleTypes}
                visibleCategories={visibleCategories}
                hoveredType={hoveredType}
                setHoveredType={setHoveredType}
                handleTypeHover={(type) => setHoveredType(type)}
                handleTypeUnhover={() => setHoveredType(null)}
                hoveredPoiId={hoveredPoiId}
                setHoveredPoiId={setHoveredPoiId}
                expandedCategories={expandedCategories}
                setExpandedCategories={setExpandedCategories}
                expandedTypes={expandedTypes}
                setExpandedTypes={setExpandedTypes}
                toggleCategory={(catId) => {
                  setVisibleCategories(old => {
                    const next = new Set(old);
                    if (next.has(catId)) next.delete(catId);
                    else next.add(catId);
                    return next;
                  });
                }}
                toggleType={(typeId) => {
                  setVisibleTypes(old => {
                    const next = new Set(old);
                    if (next.has(typeId)) next.delete(typeId);
                    else next.add(typeId);
                    return next;
                  });
                }}
                handleMouseDown={handleSidebarMouseDown}
                pois={offlinePoisWithNames}
                tipUrl={bundle.meta?.tip_url}
                poiLabelToggleBtn={
                  <button
                    style={{
                      margin: '10px 0 10px 0',
                      background: showAllPoiLabels ? '#ffdc5e' : '#232323',
                      color: showAllPoiLabels ? '#232' : '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '7px 16px',
                      fontWeight: 700,
                      fontSize: 16,
                      cursor: 'pointer',
                      boxShadow: showAllPoiLabels ? '0 2px 14px #ffd70044' : 'none',
                      transition: 'background 0.16s, color 0.16s'
                    }}
                    onClick={() => { setShowAllPoiLabels(v => !v); }}
                  >
                    {showAllPoiLabels ? "Hide all POI labels" : "Show all POI labels"}
                  </button>
                }
                flashType={flashType}
                setFlashType={setFlashType}
                zones={zones}
                visibleZones={visibleZones}
                setVisibleZones={setVisibleZones}
                toggleZone={() => { }}
                toggleAllZones={() => { }}
                zonesExpanded={zonesExpanded}
                toggleZonesExpand={() => setZonesExpanded(v => !v)}
                hoveredZoneId={hoveredZoneId}
                setHoveredZoneId={setHoveredZoneId}
                hideSidebarHover={hideSidebarHover}
                setHideSidebarHover={setHideSidebarHover}
                extraRegionMaps={extraRegionMaps}
                isMapOwner={true}
                onAddNewRegionMap={null}
                onRegionMapClick={null}
                parentMapId={bundle.id}
                styles={bundle.styles || {}}
                stylesLoading={false}
                stylesError={null}
              />
            </div>
          </div>
        )}

        <SaveLayerModal
          open={showSaveLayerModal}
          onSave={({ name, isPublic }) => {
            setPendingLayer({ poi_name: name, isPublic });
            setShowSaveLayerModal(false);
          }}
          onCancel={() => { setShowSaveLayerModal(false); }}
        />

        {stylerOpen && (
          <Styler
            mapId={bundle.id}
            user={null}
            open={stylerOpen}
            onClose={() => { setStylerOpen(false); }}
            styles={bundle.styles}
            saveStyles={saveStyles}
          />
        )}

        {stylerOfflineInfoOpen && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.55)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              background: '#252532',
              color: '#ffe089',
              borderRadius: 16,
              boxShadow: '0 2px 36px #0009',
              padding: '34px 40px 26px 40px',
              maxWidth: 400,
              textAlign: 'center',
              fontSize: 18,
              border: '2.5px solid #886'
            }}>
              <div style={{ fontWeight: 900, fontSize: 22, marginBottom: 9 }}>Feature Unavailable in Local Mode</div>
              <div style={{ marginBottom: 17 }}>
                This feature is not available in local mode.<br />
                To enable changing styles of your map, please login and upload this map to the cloud servers.<br /><br />
                <span style={{ color: '#ffe089', fontSize: 15, fontWeight: 400 }}>
                  Why? Advanced style changes require server-side storage.<br />
                  Saving too much design data in your browser could slow things down or risk data loss.<br />
                  Sign in and upload for full access, zero cost.
                </span>
              </div>
              <button
                onClick={() => { setStylerOfflineInfoOpen(false); }}
                style={{
                  background: '#ffe089',
                  color: '#212326',
                  fontWeight: 800,
                  border: 'none',
                  borderRadius: 8,
                  padding: '10px 30px',
                  fontSize: 16,
                  cursor: 'pointer',
                  marginTop: 8,
                  boxShadow: '0 2px 9px #0003',
                  letterSpacing: 1
                }}
              >Close</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default OfflineMapView;
